import { useState } from "react";
import { TextField, Typography, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useForm } from "react-hook-form";
import axiosInstance from "../utils/axiosConfig";
import Button from "../Input/Button";

/**
 * ResetPasswordRequest Component
 * 
 * SIMPLIFIED EMAIL-ONLY FLOW (Updated October 20, 2025)
 * 
 * This component now only requires the parent's email address.
 * If a child is locked out and can't remember their username, they can still
 * request a password reset using just the parent's email.
 * 
 * CURRENT FLOW:
 * 1. Child enters parent's email address
 * 2. Backend finds all children associated with that parent email
 * 3. Email sent to parent lists ALL children's usernames
 * 4. Parent helps child reset password using the link
 * 
 * COMMENTED OUT: Username field (two-factor validation)
 * Reason: Simplified for better UX when child forgets username
 * Date Commented: October 20, 2025
 * 
 * To re-enable username validation:
 * - Uncomment the username field code below
 * - Update backend validation to require username
 * - Update API to validate username matches child
 * 
 * SECURITY:
 * - Backend validates parent email exists in database
 * - Generic success message prevents email enumeration attacks
 * - Email sent to parent lists all children for context
 * 
 * STYLING:
 * - Matches ProductionRegistration.jsx design (BridgePal colors)
 * - Orange background (#FF6419) with green accents (#B4FA64)
 * - Consistent footer with other public pages
 */

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundImage: 'linear-gradient(to bottom, #3f51b5, #ffffff)',
        overflow: 'hidden', // Prevent scrolling
    },
    contentArea: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: theme.spacing(1, 0),
    },
    registrationContainer: {
        width: '480px',
        maxWidth: '85%',
        backgroundColor: '#FF6419', // BridgePal orange
        boxShadow: theme.shadows[5],
        padding: theme.spacing(1.5, 3.5, 1.5),
        borderRadius: 12,
        outline: 'none',
    },
    title: {
        color: '#B4FA64', // BridgePal green
        textAlign: 'center',
        padding: '12px 24px',
        fontSize: 30,
        fontFamily: 'NeueHassDisplayMedium',
    },
    input: {
        '& .MuiOutlinedInput-root': {
            background: 'white',
            fontFamily: 'NeueHassDisplayMedium',
            borderRadius: 16,
        },
    },
    submitButton: {
        width: '80%',
        textTransform: 'capitalize',
        backgroundColor: '#B4FA64',
        color: '#FF6419',
        borderRadius: '24px',
        fontFamily: 'NeueHassDisplayRoman',
        '& .MuiButton-label': {
            fontSize: '19px',
        },
    },
    helperText: {
        color: '#B4FA64',
        fontSize: '14px',
        marginTop: '8px',
        textAlign: 'center',
    },
    successText: {
        color: '#B4FA64',
        fontSize: '18px',
        marginTop: '20px',
        textAlign: 'center',
        lineHeight: '1.6',
    },
    footer: {
        backgroundColor: '#b4fa64',
        width: '100%',
        padding: theme.spacing(2.5),
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    footerLink: {
        color: '#ff6419',
        textDecoration: 'underline',
        marginRight: theme.spacing(2.5),
        marginLeft: theme.spacing(2.5),
        marginBottom: theme.spacing(1),
        fontSize: '24px',
        fontWeight: 900,
        fontFamily: '"Lato", "NeueHaasGroteskDisplay Pro", Arial, sans-serif',
        '&:hover': {
            textDecoration: 'underline'
        }
    },
}));

export default function ResetPasswordRequest() {
    const classes = useStyles();
    const { register, handleSubmit, formState: { errors } } = useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    
    /**
     * Handle form submission
     * 
     * Sends password reset request to backend with username and parent email.
     * On success, displays success message and redirects to WIX after 3 seconds.
     * On error, displays alert with error message.
     */
    const onSubmit = async (data) => {
        setIsSubmitting(true);
        
        try {
            const response = await axiosInstance.post('/auth/password-reset-request', data);
            
            if (response.status === 200) {
                console.log('Password reset request successful');
                setSubmitSuccess(true);
                
                // Show success message for 3 seconds, then redirect to WIX
                setTimeout(() => {
                    window.location.href = 'https://www.bridgepalkids.com/';
                }, 3000);
            }
        } catch (error) {
            console.error('Password reset request error:', error);
            const errorMessage = error.response?.data?.message || 'An error occurred. Please try again.';
            alert(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    /**
     * Success state view
     * 
     * Displayed after successful form submission.
     * Shows confirmation message and auto-redirects to WIX.
     */
    if (submitSuccess) {
        return (
            <div className={classes.root}>
                <div className={classes.contentArea}>
                    <div className={classes.registrationContainer}>
                        <Typography variant="h5" className={classes.title}>
                            Reset Link Sent!
                        </Typography>
                        <Typography className={classes.successText}>
                            A password reset email has been sent to your parent's email address.
                        </Typography>
                        <Typography className={classes.successText}>
                            Please check your parent's inbox and click the link to reset your password.
                        </Typography>
                        <Typography className={classes.helperText} style={{ marginTop: '20px', fontSize: '12px' }}>
                            Redirecting you back to login...
                        </Typography>
                    </div>
                </div>
                <footer className={classes.footer}>
                    <a href="https://xcbrgpal.com" className={classes.footerLink}>BridgePal Website</a>
                    <a href="https://bridgepalkids.com/terms" className={classes.footerLink}>Terms of Use</a>
                    <a href="https://bridgepalkids.com/privacy-policy" className={classes.footerLink}>Privacy Policy</a>
                    <a href="https://bridgepalkids.com/kids-privacy-policy" className={classes.footerLink}>Kids Privacy</a>
                </footer>
            </div>
        );
    }
    
    /**
     * Main form view
     * 
     * Two-field form requiring:
     * - Username (child's display_name)
     * - Parent email (for verification)
     */
    return (
        <div className={classes.root}>
            <div className={classes.contentArea}>
                <div className={classes.registrationContainer}>
                    <Typography variant="h5" className={classes.title}>
                        Reset Password
                    </Typography>
                    
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    placeholder="Your Username"
                                    variant="outlined"
                                    fullWidth
                                    {...register("username", { required: "Username is required" })}
                                    className={classes.input}
                                    error={!!errors.username}
                                    helperText={errors.username?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    placeholder="Parent's Email Address"
                                    variant="outlined"
                                    fullWidth
                                    type="email"
                                    {...register("parent_email", { 
                                        required: "Parent's email is required",
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: "Invalid email address"
                                        }
                                    })}
                                    className={classes.input}
                                    error={!!errors.parent_email}
                                    helperText={errors.parent_email?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Typography className={classes.helperText} style={{ color: '#ffffff', fontSize: '16px' }}>
                                    Enter your username and your parent's email address. 
                                    A password reset link will be sent to your parent's email.
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={12} style={{ textAlign: 'center' }}>
                                <Button
                                    className={classes.submitButton}
                                    type="submit"
                                    disabled={isSubmitting}
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </div>
            </div>
            
            <footer className={classes.footer}>
                <a href="https://xcbrgpal.com" className={classes.footerLink}>BridgePal Website</a>
                <a href="https://bridgepalkids.com/terms" className={classes.footerLink}>Terms of Use</a>
                <a href="https://bridgepalkids.com/privacy-policy" className={classes.footerLink}>Privacy Policy</a>
                <a href="https://bridgepalkids.com/kids-privacy-policy" className={classes.footerLink}>Kids Privacy</a>
            </footer>
        </div>
    );
}
